<template>
  <div>
    <div style="display: flex; visibility: hidden">
      <slot></slot>
    </div>
    <slot name="tooltip"></slot>
  </div>
</template>

<script>
import {createId} from '../../utils/string';

let mutationObserver;

const isShow = (style) => {
  return !(style.indexOf('display: none;') > -1);
};

export default {
  name: 'custom-item',
  props: {
    id: [String, Number],
    show: {
      type: Boolean,
      default: true,
    },
    content: {
      type: String,
      default: '',
    },
    angle: {
      type: Number,
      default: 0,
    },
    draggable: {
      type: Boolean,
      default: false,
    },
    width: [String, Number],
    height: [String, Number],
    objectId: [String, Number],
    offsetX: Number,
    offsetY: Number,
    opacity: {
      type: Number,
      default: 1,
    },
    visibleDistance: Number,
    position: {type: Object, required: true},
    depthTest: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      instance: null,
      instanceId: null,
    };
  },
  computed: {
    viewer3D() {
      return this.bimface.viewer3D;
    },
    model3D() {
      return this.model.model3D;
    },
  },
  inject: {
    bimface: {
      default: {
        drawableMng: null,
      },
    },
    model: {
      default: {
        model3D: null,
      },
    },
  },
  watch: {
    show(newVal) {
      this.showOrHide(newVal);
    },
    depthTest(newVal) {
      this.toggleDepthTest(newVal);
    },
    content(newVal) {
      if (!Object.keys(this.$slots).length) {
        this.instance.setContent(newVal);
      }
    },
    width(newVal) {
      this.instance && this.instance.setWidth(newVal);
    },
    height(newVal) {
      this.instance && this.instance.setHeight(newVal);
    },
    offsetX(newVal) {
      this.instance && this.instance.setOffsetX(newVal);
    },
    offsetY(newVal) {
      this.instance && this.instance.setOffsetY(newVal);
    },
    opacity(newVal) {
      this.instance && this.instance.setOpacity(newVal);
    },
    visibleDistance(newVal) {
      this.instance && this.instance.setVisibleDistance(newVal);
    },
    position(newVal) {
      this.instance && this.instance.setWorldPosition(newVal);
    },
    'bimface.drawableMng': function(newVal, oldVal) {
      if (newVal && !this.instance) {
        this.init(newVal, this.viewer3D, this.model3D);
      }
    },
    'model.model3D': function(newVal, oldVal) {
      if (this.bimface.drawableMng && !this.instance && newVal) {
        this.init(this.bimface.drawableMng, this.viewer3D, newVal);
      }
    },
  },
  // bug 为挂载在模型下面时，会导致报错
  mounted() {
    if (this.bimface.drawableMng && this.instance === null) {
      this.init(this.bimface.drawableMng, this.viewer3D, this.model3D);
    }
  },
  methods: {
    init(drawableMng, viewer3D, model3D = null) {
      this.drawableMng = drawableMng;

      let config = new Glodon.Bimface.Plugins.Drawable.CustomItemConfig();
      this.angle && (config.angle = this.angle);
      this.width && (config.width = this.width);
      this.height && (config.height = this.height);
      this.depthTest && (config.enableDepthTest = this.depthTest);
      this.draggable && (config.draggable = this.draggable);
      this.objectId && (config.objectId = this.objectId);
      this.offsetX && (config.offsetX = this.offsetX);
      this.offsetY && (config.offsetY = this.offsetY);
      this.opacity && (config.opacity = this.opacity);
      this.visibleDistance && (config.visibleDistance = this.visibleDistance);
      this.position && (config.worldPosition = this.position);
      model3D && (config.modelId = model3D.modelId);

      config.viewer = viewer3D;
      this.instanceId = this.id || createId();
      config.id = this.instanceId;

      let el;
      // 设置标签内容
      if (!Object.keys(this.$slots).length) {
        let wrapper = document.createElement('div');
        wrapper.innerText = this.content;
        el = wrapper;
        config.content = wrapper;
      } else {
        // console.log(this.$slots.default[0].elm.clientWidth);
        let width = 0;
        let height = 0;
        if (this.$slots.default.length > 1) {
          // 多个子元素的情况，取最大一个子元素的宽度
          let wrapper = document.createElement('div');
          for (const vNode of this.$slots.default) {
            if (vNode.elm.offsetWidth > width) width = vNode.elm.offsetWidth;
            if (vNode.elm.offsetHeight > height)
              height = vNode.elm.offsetHeight;
            wrapper.appendChild(vNode.elm);
          }
          el = wrapper;
          config.content = wrapper;
        } else {
          width = this.$slots.default[0].elm.offsetWidth;
          height = this.$slots.default[0].elm.offsetHeight;
          config.content = this.$slots.default[0].elm;
          el = this.$slots.default[0].elm;
        }
        // 设置宽高
        !this.width && (config.width = width);
        !this.height && (config.height = height);
      }

      this.instance = new Glodon.Bimface.Plugins.Drawable.CustomItem(config);

      // 注册标签的显示隐藏观察器
      mutationObserver = new MutationObserver((mutations, observer) => {
        // console.log(mutations);
        for (const mutation of mutations) {
          if (mutation.attributeName !== 'style') continue;
          let oldShow = isShow(mutation.oldValue);
          if (!oldShow && !mutation.target.style.display) {
            this.$emit('display');
          } else if (oldShow && mutation.target.style.display === 'none') {
            this.$emit('hide');
          }
        }
      });
      mutationObserver.observe(el.parentNode.parentNode, {
        attributes: true,
        attributeOldValue: true,
      });

      drawableMng.addItem(this.instance);

      this.showOrHide(this.show);

      // 注册事件
      this.instance.onClick &&
      this.instance.onClick(() => {
        this.$emit('click', {
          instance: this.instance,
          drawableContainer: arguments[0],
          model3D: arguments[1],
          viewer3D: arguments[2],
        });
      });

      this.instance.onDoubleClick &&
      this.instance.onDoubleClick(() => {
        this.$emit('doubleClick', {
          instance: this.instance,
          drawableContainer: arguments[0],
          model3D: arguments[1],
          viewer3D: arguments[2],
        });
      });

      this.instance.onEndDrag &&
      this.instance.onEndDrag(() => {
        this.$emit('dragEnd', {
          instance: this.instance,
          drawableContainer: arguments[0],
          model3D: arguments[1],
          viewer3D: arguments[2],
        });
      });

      this.instance.onRightClick &&
      this.instance.onRightClick(() => {
        this.$emit('rightClick', {
          instance: this.instance,
          drawableContainer: arguments[0],
          model3D: arguments[1],
          viewer3D: arguments[2],
        });
      });

      this.instance.onObstructionChanged &&
      this.instance.onObstructionChanged((isObstructed) => {
        this.$emit('obstructionChanged', {
          instance: this.instance,
          status: isObstructed,
          drawableContainer: this.drawableMng,
          model3D: this.model3D,
          viewer3D: this.viewer3D,
        });
      });
    },
    showOrHide(state) {
      if (this.instance) {
        state ? this.instance.show() : this.instance.hide();
      }
    },
    toggleDepthTest(status) {
      this.instance && this.instance.enableDepthTest(status);
    },
  },
  beforeDestroy() {
    if (this.drawableMng && this.instance) {
      this.drawableMng.removeItemById(this.instanceId);
    }
  },
};
</script>

<style lang="scss" scoped></style>
